// Shared utilities for forecast table components
import { hasRateChanged } from '../../Utilities/distributionRateUtils';

export interface WeeklyDistributionRate {
    week: string;
    unitCost?: number | null;
    rateOfSale?: number | null;
    distribution?: number | null;
    forecastWeek: {
        month: number;
    };
    hasUnitCostOverride?: boolean;
    hasDistributionOverride?: boolean;
    hasRateOfSaleOverride?: boolean;
    hasDistributionRateOverride?: boolean;
}

export interface SkuBaseForecastRow {
    id: string;
    skuDescription: string;
    weeklyDistributionRates: WeeklyDistributionRate[];
}

export interface PromotionProduct {
    skuBaseForecastId: string;
    weeklyDistributionRates?: string[];
}

export interface SnackbarState {
    children: string;
    severity: 'success' | 'error' | 'warning' | 'info';
}

export interface EditedRow {
    original: SkuBaseForecastRow;
    updated: SkuBaseForecastRow;
}

export interface UnitCostOverride {
    skuBaseForecastId: string;
    weekStart: string;
    unitCost: number | null;
}

export interface RateOfSaleOverride {
    skuBaseForecastId: string;
    weekStart: string;
    rateOfSale: number | null;
}

export interface DistributionOverride {
    skuBaseForecastId: string;
    weekStart: string;
    distribution: number | null;
}

export interface ApiError {
    response?: {
        data?: string;
    };
    message?: string;
}

// Utility function for currency formatting
export const formatCurrency = (value: number | string | undefined | null): string => {
    const numValue = parseFloat(value?.toString() || '0');
    const formattedValue = numValue.toFixed(3);
    return formattedValue.endsWith('0') 
        ? `£${numValue.toFixed(2)}` 
        : `£${numValue.toFixed(3)}`;
};

// Utility function to safely parse numeric values
export const safeParseFloat = (value: number | string | undefined | null): number => {
    return parseFloat(value?.toString() || '0');
};

// Utility function to update weekly distribution rates from row data
export const updateWeeklyDistributionRates = <T extends keyof WeeklyDistributionRate>(
    row: SkuBaseForecastRow,
    weekField: string,
    value: string,
    fieldName: T
): SkuBaseForecastRow => {
    const updatedRow = {
        ...row,
        weeklyDistributionRates: row.weeklyDistributionRates.map(w =>
            w.week === weekField
                ? { ...w, [fieldName]: safeParseFloat(value) }
                : w
        )
    };

    return updatedRow;
};

// Generic function to get changed values for any field
export const getChangedValues = <T extends keyof WeeklyDistributionRate>(
    newRates: WeeklyDistributionRate[],
    oldRates: WeeklyDistributionRate[],
    fieldName: T
): Array<Omit<UnitCostOverride | RateOfSaleOverride | DistributionOverride, 'skuBaseForecastId'>> => {
    const changes: Array<Omit<UnitCostOverride | RateOfSaleOverride | DistributionOverride, 'skuBaseForecastId'>> = [];

    const overrideFlagMap: Record<string, string> = {
        'distribution': 'hasDistributionOverride',
        'rateOfSale': 'hasRateOfSaleOverride',
        'unitCost': 'hasUnitCostOverride',
        'actualDistributionRate': 'hasDistributionRateOverride'
    };

    const overrideFlagName = overrideFlagMap[fieldName as string];

    for (const newRate of newRates) {
        const oldRate = oldRates.find(rate => rate.week === newRate.week);
        if (oldRate) {
            const oldValue = safeParseFloat(oldRate[fieldName] as any);
            const newValue = safeParseFloat(newRate[fieldName] as any);
            const oldHasOverride = (oldRate as any)[overrideFlagName] || false;
            const newHasOverride = (newRate as any)[overrideFlagName] || false;

            if (hasRateChanged(oldValue, newValue, oldHasOverride, newHasOverride)) {
                // If override flag changed from true to false, this is a reset - send null
                const valueToSend = (oldHasOverride && !newHasOverride) ? null : newValue;
                changes.push({
                    weekStart: newRate.week,
                    [fieldName]: valueToSend
                } as any);
            }
        }
    }
    return changes;
};

// Specific functions for backward compatibility
export const getChangedUnitCosts = (newRates: WeeklyDistributionRate[], oldRates: WeeklyDistributionRate[]): Omit<UnitCostOverride, 'skuBaseForecastId'>[] => {
    return getChangedValues(newRates, oldRates, 'unitCost') as Omit<UnitCostOverride, 'skuBaseForecastId'>[];
};

export const getChangedRateOfSales = (newRates: WeeklyDistributionRate[], oldRates: WeeklyDistributionRate[]): Omit<RateOfSaleOverride, 'skuBaseForecastId'>[] => {
    return getChangedValues(newRates, oldRates, 'rateOfSale') as Omit<RateOfSaleOverride, 'skuBaseForecastId'>[];
};

export const getChangedDistributions = (newRates: WeeklyDistributionRate[], oldRates: WeeklyDistributionRate[]): Omit<DistributionOverride, 'skuBaseForecastId'>[] => {
    return getChangedValues(newRates, oldRates, 'distribution') as Omit<DistributionOverride, 'skuBaseForecastId'>[];
};

// Common validation functions
export const validateUnitCost = (value: number): boolean => {
    return value > 0 && value <= 999999.999;
};

export const validateRateOfSale = (value: number): boolean => {
    return value >= 0 && value <= 999999.999;
};

// Common error handling
export const handleApiError = (error: unknown): string => {
    const apiError = error as ApiError;
    return apiError.response?.data || apiError.message || 'An unexpected error occurred';
};
